import { Inject, inject, Injectable, InjectionToken, Optional } from '@angular/core';
import {
  Organization,
  type OrganizationRepository,
  type CaseRepository,
  ILicenseInfo,
  type AffiliateRepository,
  type DeviceRepository,
} from '@telespot/domain';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import { EMPTY, from, merge, Observable, of, Subject } from 'rxjs';
import { RegisterOrganization, RegisterOrganizationHandler } from '@telespot/management/use-cases';
import { AuthService } from '@telespot/web-core';
import { PaginatedStore, PaginatedStateLoadingMode, PaginatedStateModel } from '@shared/ui';
import { EntityState } from '@ngrx/entity';
import { concatLatestFrom } from '@ngrx/operators';

export interface OrganizationData {
  id: string;
  name: string;
  logoUrl?: string;
  licenseInfo: ILicenseInfo;
  usedStorage: number;
  members: number | undefined;
  numCases: number | undefined;
  numDevices: number | undefined;
  isCurrent: boolean;
}

export interface OrganizationStoreState extends PaginatedStateModel<OrganizationData> {
  // User filter
  filterByUser: string | undefined;

  // designated current organization
  currentOrganizationId: string | undefined;

  // organization editor/creator
  currentOrganization?: OrganizationData;

  // organization-specific error handling
  registrationError: string | undefined;
}

export const ORGANIZATION_REPOSITORY = new InjectionToken<OrganizationRepository>('ORGANIZATION_REPOSITORY');
export const ORG_STORE_DEVICE_REPOSITORY = new InjectionToken<DeviceRepository>('ORG_STORE_DEVICE_REPOSITORY');
export const AFFILIATE_REPOSITORY = new InjectionToken<AffiliateRepository>('AFFILIATE_REPOSITORY');
export const CASE_REPOSITORY = new InjectionToken<CaseRepository>('CASE_REPOSITORY');
export const REGISTER_ORG_HANDLER = new InjectionToken<RegisterOrganizationHandler>('REGISTER_ORG_HANDLER');
export const ORGANIZATION_REPOSITORY_LOADING_MODE = new InjectionToken<PaginatedStateLoadingMode>(
  'ORGANIZATION_REPOSITORY_LOADING_MODE'
);
export const IS_NEW_ORGANIZATION = new InjectionToken<boolean>('IS_NEW_ORGANIZATION');
/**
 * Controls whether this OrganizationStore instance subscribes to
 * `AuthService.currentOrganization$` and mirrors the auth-selected org as its
 * `currentOrganization`. Defaults to `true`, which is what the navbar (and any
 * caller that wants to track the user's active org) needs.
 *
 * Components that own the displayed organization themselves — e.g. the
 * organization detail page (id from route) or the organizations list (shows
 * many) — should provide `false`. Otherwise the store races between the
 * auth-driven fetch and the route-driven fetch, and any subsequent
 * `authService.setOrganization(...)` overrides the displayed org.
 */
export const TRACK_AUTH_ORGANIZATION = new InjectionToken<boolean>('TRACK_AUTH_ORGANIZATION');
export const DEFAULT_PAGE_SIZE = 10;
const MAX_PARALLEL_COUNT_TASKS = 2;

@Injectable()
export class OrganizationStore extends PaginatedStore<OrganizationData, OrganizationStoreState> {
  public readonly organizationRegistered$ = new Subject<string>();

  private readonly organizationRepository = inject<OrganizationRepository>(ORGANIZATION_REPOSITORY);
  private readonly affiliateRepository = inject<AffiliateRepository>(AFFILIATE_REPOSITORY);
  private readonly deviceRepository = inject<DeviceRepository>(ORG_STORE_DEVICE_REPOSITORY);
  private readonly caseRepository = inject<CaseRepository>(CASE_REPOSITORY);
  private readonly registerOrganizationHandler = inject<RegisterOrganizationHandler>(REGISTER_ORG_HANDLER);

  constructor(
    @Inject(ORGANIZATION_REPOSITORY_LOADING_MODE) loadingMode: PaginatedStateLoadingMode,
    private readonly authService: AuthService,
    @Inject(IS_NEW_ORGANIZATION) isNewOrganization: boolean,
    @Optional() @Inject(TRACK_AUTH_ORGANIZATION) trackAuthOrganization: boolean | null
  ) {
    super(
      {
        currentOrganization: undefined,
        currentOrganizationId: undefined,
        filterByUser: undefined,
        registrationError: undefined,
      },
      {
        initialPageSize: DEFAULT_PAGE_SIZE,
        maxConcurrentCountQueries: MAX_PARALLEL_COUNT_TASKS,
        loadingMode: loadingMode,
      }
    );

    // cascading effects
    this.onUserSpecified(this.filterByUser$);
    this.onCurrentOrganizationIdChange(this.currentOrganizationId$);

    // Subscribe to auth's current organization only when both:
    // (1) this is not the new-organization flow (no org to track yet), and
    // (2) the consumer hasn't opted out via TRACK_AUTH_ORGANIZATION=false.
    //     Components that own the displayed org themselves (detail / list)
    //     opt out to avoid the race between auth-driven and consumer-driven
    //     fetches.
    const shouldTrackAuth = !isNewOrganization && trackAuthOrganization !== false;
    if (shouldTrackAuth) {
      this.onAuthOrganizationChange(
        this.authService.currentOrganization$.pipe(map((organization) => organization?.id))
      );
    }
  }

  // Expose base class selectors with original names for backwards compatibility
  public readonly organizations$ = this.items$;
  public readonly nextOrganizationToken$ = this.nextToken$;
  public readonly previousOrganizationTokens$ = this.previousTokens$;
  public readonly organizationsLoaded$ = this.itemsLoaded$;
  public readonly noOrganizations$ = this.noItems$;
  public readonly loaded$ = this.loading$.pipe(map((loading) => !loading));

  // Organization-specific selectors
  public readonly currentOrganization$ = this.select((state) => state.currentOrganization);
  public readonly currentOrganizationId$ = this.select((state) => state.currentOrganizationId);
  public readonly registrationError$ = this.select((state) => state.registrationError);
  public readonly filterByUser$ = this.select((state) => state.filterByUser);

  // Organization-specific updaters
  public readonly setFilterByUser = this.updater((state, filterByUser: string | undefined) => ({
    ...state,
    filterByUser,
  }));

  public readonly setCurrentOrganizationId = this.updater((state, currentOrganizationId: string | undefined) => {
    const allIds = state.items.ids as string[];
    return {
      ...state,
      items: this.itemsAdapter.updateMany(
        allIds.map((id) => ({
          id,
          changes: { isCurrent: currentOrganizationId ? id === currentOrganizationId : false },
        })),
        state.items
      ),
      currentOrganizationId,
    };
  });

  private readonly setRegistrationError = this.updater((state, registrationError: string | undefined) => ({
    ...state,
    registrationError,
    loading: false,
  }));

  private readonly updateCurrentOrganization = this.updater(
    (state, organization: OrganizationData | Partial<OrganizationData>) => {
      const newCurrentId = organization.id ?? state.currentOrganizationId;
      const allIds = state.items.ids as string[];
      return {
        ...state,
        currentOrganization: state.currentOrganization
          ? { ...state.currentOrganization, ...organization }
          : (organization as OrganizationData),
        items: organization.id
          ? this.itemsAdapter.updateMany(
              allIds.map((id) => ({ id, changes: { isCurrent: id === newCurrentId } })),
              state.items
            )
          : state.items,
        currentOrganizationId: newCurrentId,
      };
    }
  );

  // Alias base class effects for backwards compatibility
  public readonly countOrganizations = this.countItems;
  public readonly loadMoreOrganizations = this.loadMoreItems;
  public readonly initializeOrganizations = this.initializeItems;

  private readonly onUserSpecified = this.effect((action$: Observable<string | undefined>) =>
    action$.pipe(tap(() => this.initializeItems()))
  );

  private readonly onCurrentOrganizationIdChange = this.effect((organizationId$: Observable<string | undefined>) =>
    organizationId$.pipe(
      concatLatestFrom(() => [this.currentOrganization$]),
      tap(([organizationId, currentOrganization]: [string | undefined, OrganizationData | undefined]) => {
        if (organizationId && currentOrganization?.id !== organizationId) {
          this.fetchOrganization(organizationId);
        }
      })
    )
  );

  private readonly onAuthOrganizationChange = this.effect((organizationId$: Observable<string | undefined>) =>
    organizationId$.pipe(
      concatLatestFrom(() => [this.currentOrganization$]),
      tap(([organizationId, currentOrganization]: [string | undefined, OrganizationData | undefined]) => {
        if (currentOrganization?.id !== organizationId) {
          this.setCurrentOrganizationId(organizationId);
        }
      })
    )
  );

  public readonly fetchOrganization = this.effect((action$: Observable<string>) =>
    action$.pipe(
      switchMap((id) => from(this.organizationRepository.getById(id))),
      tap((organization: Organization) => {
        const organizationData = {
          id: organization.id,
          name: organization.name,
          logoUrl: organization.logoUrl,
          licenseInfo: organization.license,
          usedStorage: organization.usedStorage,
          members: 0,
          numCases: 0,
          numDevices: 0,
        };
        this.updateCurrentOrganization(organizationData);

        // Fetch counts for this organization
        this.fetchSingleOrganizationCounts(organization.id);
      })
    )
  );

  public readonly registerOrganization = this.effect(
    (action$: Observable<{ organizationId: string; name: string; license: ILicenseInfo; logo?: File }>) =>
      action$.pipe(
        tap(() => this.setLoading(true)),
        withLatestFrom(this.authService.currentUser$),
        switchMap(([{ organizationId, name, license, logo }, currentUser]) =>
          from(
            this.registerOrganizationHandler.execute(
              new RegisterOrganization({
                organizationId,
                name,
                license,
                logo,
                userId: currentUser.id,
              })
            )
          )
        ),
        tap(async (organizationId) => {
          // Manage state in the auth service
          const isSameOrganization = this.authService.currentOrganizationValue.id === organizationId;
          if (isSameOrganization) await this.authService.setOrganizationFromId(organizationId);
          this.authService.updateUserRolesAndOrganizations();

          // Trigger organization registered event
          this.organizationRegistered$.next(organizationId);
          this.setLoading(false);
        }),
        catchError((error) => {
          this.setRegistrationError(error.message);
          return EMPTY;
        })
      )
  );

  private readonly fetchSingleOrganizationCounts = this.effect((action$: Observable<string>) =>
    action$.pipe(
      switchMap((orgId) =>
        from(this.fetchOrganizationCountsData(orgId)).pipe(
          tap(([members, numCases, numDevices]) => {
            // Update current organization with partial data
            this.updateCurrentOrganization({
              members,
              numCases,
              numDevices,
            });
          }),
          catchError((error) => {
            console.error(`Failed to fetch counts for organization ${orgId}:`, error);
            return EMPTY;
          })
        )
      )
    )
  );

  // Helper methods to fetch counts for an organization
  private fetchAffiliateCount(orgId: string): Observable<number> {
    return from(this.affiliateRepository.count(orgId)).pipe(
      map((members) => members),
      catchError((error) => {
        console.error(`Failed to fetch affiliate count for ${orgId}:`, error);
        return of(0);
      })
    );
  }

  private fetchCaseCount(orgId: string): Observable<number> {
    return from(this.caseRepository.countByOrganization(orgId)).pipe(
      map((numCases) => numCases),
      catchError((error) => {
        console.error(`Failed to fetch case count for ${orgId}:`, error);
        return of(0);
      })
    );
  }

  private fetchDeviceCount(orgId: string): Observable<number> {
    return from(this.deviceRepository.count(orgId)).pipe(
      map((numDevices) => numDevices),
      catchError((error) => {
        console.error(`Failed to fetch device count for ${orgId}:`, error);
        return of(0);
      })
    );
  }

  private fetchOrganizationCountsData(orgId: string): Promise<[number, number, number]> {
    return Promise.all([
      this.fetchAffiliateCount(orgId).toPromise(),
      this.fetchCaseCount(orgId).toPromise(),
      this.fetchDeviceCount(orgId).toPromise(),
    ]);
  }

  // Implement abstract methods from PaginatedStore
  protected async countRepository(searchTerm?: string): Promise<number> {
    const userID = this.get((state) => state.filterByUser);
    return this.organizationRepository.count(searchTerm, userID);
  }

  /**
   * Delete repository item.
   * The organization store does not support deletion.
   * Returning false will invalidate any posterior item removal operations.
   * @param _itemId The ID of the item to remove (not used in this case)
   * @returns Promise that lets you know if the operation was successful, always false in this case
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected async deleteRepositoryItem(_itemId: string): Promise<boolean> {
    return false;
  }

  protected listFlow(
    limit: number,
    lastItem: OrganizationData | undefined,
    searchTerm: string | undefined
  ): Observable<OrganizationData[]> {
    const userID = this.get((state) => state.filterByUser);
    return from(this.organizationRepository.list(searchTerm ?? '', limit, lastItem?.name, userID)).pipe(
      map((organizations: Organization[]) => {
        // Build organizations with initial values
        return organizations.map((organization) => ({
          id: organization.id,
          name: organization.name,
          logoUrl: organization.logoUrl,
          licenseInfo: organization.license,
          usedStorage: organization.usedStorage,
          members: undefined,
          numCases: undefined,
          numDevices: undefined,
          isCurrent: false,
        }));
      })
    );
  }

  protected processDeferredAttributes(itemId: string): Observable<Partial<OrganizationData>> {
    const members$ = this.fetchAffiliateCount(itemId).pipe(map((members) => ({ members })));
    const cases$ = this.fetchCaseCount(itemId).pipe(map((numCases) => ({ numCases })));
    const devices$ = this.fetchDeviceCount(itemId).pipe(map((numDevices) => ({ numDevices })));
    return merge(members$, cases$, devices$);
  }

  /**
   * Helper method to transform the items before they are updated.
   * Organizations are updated to mark the current organization with isCurrent: true and all other organizations with isCurrent: false.
   * @param items The items to transform
   * @param state The state of the store
   * @returns The updated items with the current organization marked with isCurrent: true and all other organizations marked with isCurrent: false
   */
  protected itemsUpdateTransform(
    items: EntityState<OrganizationData>,
    state: OrganizationStoreState
  ): EntityState<OrganizationData> {
    const currentOrganizationID = state.currentOrganizationId;
    if (currentOrganizationID) {
      const allIds = items.ids as string[];
      return this.itemsAdapter.updateMany(
        allIds.map((id) => ({ id, changes: { isCurrent: id === currentOrganizationID } })),
        items
      );
    }

    return items;
  }
}
